import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { ROUTES } from "@padi/lib/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { req } from "@padi/lib/api";
import { track, identify } from "@padi/lib/analytics";

interface OrderPayload {
  listingId: string;
  quantityInTon: number;
}

interface OrderResponse {
  id: string;
  status: string;
  message?: string;
}

// `/auth/profile` (used by useAuth()) doesn't return email/firstName — the
// `profiles` table has no such columns. `/auth/user` does, since it reads
// from the users table instead.
type AuthUser = {
  email: string;
  firstName: string;
};

export const useOrderCrop = () => {
  const router = useRouter();
  const { data: authUser } = useQuery({
    queryKey: ["auth", "user", "email-lookup"],
    queryFn: () => req<AuthUser>("/auth/user"),
    staleTime: 5 * 60 * 1000,
  });

  return useMutation<OrderResponse, Error, OrderPayload>({
    mutationFn: async (payload) => {
      return await req.post("/private/buyers/orders", {
        ...payload,
        acceptedPolicyIds: [
          "9fede575-8347-4178-9537-4968997e658d",
          "b6e88c3e-f2cd-42b6-bb38-fa74843d22f1",
          "5bdb71c1-d1fb-4377-b99c-bc7cabd7bf27",
          "413a9460-80ac-4fab-af67-d41ee2d2774e",
        ],
      });
    },
    onSuccess: (data, variables) => {
      if (authUser?.email) {
        identify(authUser.email, { FIRSTNAME: authUser.firstName });
      }
      track("order_placed", {
        orderId: data.id,
        listingId: variables.listingId,
        quantityInTon: variables.quantityInTon,
      });
      toast.success("Order Successful! Redirecting...", {
        id: "order-success",
      });
      router.push(`${ROUTES.ORDERS}/${data.id}`);
    },
  });
};
